import { DisplayMode } from '@flywire/internal-components/model';
import IframeMessageBroker from '@flywire/internal-components/services/iframeMessageBroker/iframeMessageBroker';
import type { InjectPaymentElementAppearanceSettings } from '@flywire/internal-components/services/iframeMessageBroker/model';
import { MESSAGES } from '@flywire/internal-components/services/iframeMessageBroker/model';

import {
  type InternalElementFactoryOptions,
  type PaymentInstructionsElementPublicOptions,
  SdkElement
} from '../../../model';
import { EventBus, type EventBusType } from '../../EventBus';
import { createIframe } from '../createIframe';
import { paymentInstructionsUrlBuilder } from './paymentInstructionsUrlBuilder';

export type PaymentInstructionsElementOptions = PaymentInstructionsElementPublicOptions &
  InternalElementFactoryOptions;

export const PaymentInstructionsElement = (
  options: PaymentInstructionsElementOptions
): SdkElement => {
  const { displayMode = 'full-screen', token, paymentReference } = options;
  const eventBus = EventBus();

  const mount = (id?: string) => {
    const url = paymentInstructionsUrlBuilder({
      token,
      paymentReference,
      displayMode
    });

    const { iframe, container } = createIframe({
      containerId: id,
      src: url,
      displayMode,
      title: 'Flywire Payment Instructions Element'
    });

    sendAppearanceValuesToIframe(
      IframeMessageBroker.getInstance(),
      iframe,
      options.appearance,
      eventBus
    );

    setupMessageHandlers({ container, eventBus, displayMode });
  };

  return {
    mount,
    onEvent: (key, fn) => eventBus.addEvent(key, fn),
    registeredEvents: eventBus.registeredEvents
  };
};

const sendAppearanceValuesToIframe = (
  broker: ReturnType<(typeof IframeMessageBroker)['getInstance']>,
  iframe: HTMLIFrameElement,
  appearance: InternalElementFactoryOptions['appearance'],
  eventBus: EventBusType
) => {
  broker.addListener(MESSAGES.PAYMENT_ELEMENT_LOADED, () => {
    eventBus.onEvent('loaded', {});
    if (appearance) {
      broker.sendMessage<InjectPaymentElementAppearanceSettings>(
        MESSAGES.INJECT_PAYMENT_ELEMENT_APPEARANCE_SETTINGS,
        { appearance: { ...appearance } },
        iframe
      );
    }
  });
};

function setupMessageHandlers({
  eventBus,
  container,
  displayMode
}: {
  eventBus: ReturnType<typeof EventBus>;
  container: HTMLElement;
  displayMode: DisplayMode;
}) {
  const onErrorCallback = (data: unknown) => {
    eventBus.onEvent('error', data);
    if (displayMode === 'full-screen') {
      container.remove();
    }
  };

  const onSuccessCallback = (data: unknown) => {
    eventBus.onEvent('success', data);
    if (displayMode === 'full-screen') {
      container.remove();
    }
  };

  addMessageHandler(onErrorCallback, onSuccessCallback);
}

declare const FLYWIREJS_DEBUG: boolean;
function addMessageHandler(onError: (data: unknown) => void, onSuccess: (data: unknown) => void) {
  window.addEventListener('message', event => {
    if (/\.flywire\.(com|lol|cc)/.test(event.origin) || FLYWIREJS_DEBUG) {
      const result = event.data;
      if (result.source !== 'checkout_session') {
        return;
      }
      if (result.success) {
        onSuccess(result);
      } else {
        onError(result);
      }
    }
  });
}
